




























































































import { Vue, Component, Watch } from 'vue-property-decorator'
import { LatLng } from 'leaflet'
import { LMap, LTileLayer, LControl, LPolygon } from 'vue2-leaflet'
import CovJsonLayer from './CovJsonLayer.vue'
import { divergingPalette, turboPalette, rainPalette } from '@/lib/palettes'
import { TrendView } from '@/store/state'
import { MutationTypes } from '@/store/mutations'
import * as CovJSON from 'covjson-reader'
import { contours } from 'd3-contour'

@Component({
	name: 'Map',
	components: {
		LMap,
		LTileLayer,
		LControl,
		LPolygon,
		CovJsonLayer
	}
})
export default class Map extends Vue {
	readonly bounds: LatLng[] = [new LatLng(-90, -180), new LatLng(90, 180)]
	readonly divergingPalette = divergingPalette
	readonly turboPalette = turboPalette
	readonly rainPalette = rainPalette

	get showClimatology() {
		return this.trendView === 'clim'
	}

	get showSeasonalAnomalies() {
		return this.trendView === 'seasAnoms'
	}

	set showSeasonalAnomalies(value: boolean) {
		if (value) {
			this.trendView = 'seasAnoms'
		} else {
			this.trendView = 'clim'
		}
	}

	get showENAnomalies() {
		return this.trendView === 'enAnoms'
	}

	set showENAnomalies(value: boolean) {
		if (value) {
			this.trendView = 'enAnoms'
		} else {
			this.trendView = 'clim'
		}
	}

	get showLNAnomalies() {
		return this.trendView === 'lnAnoms'
	}

	set showLNAnomalies(value: boolean) {
		if (value) {
			this.trendView = 'lnAnoms'
		} else {
			this.trendView = 'clim'
		}
	}

	get trendView() {
		return this.$tStore.state.trendView
	}

	set trendView(value: TrendView) {
		this.$tStore.commit(MutationTypes.SET_TREND_VIEW, value)
	}

	get selectedSeasonIndex() {
		return this.$tStore.state.selectedSeasonIndex
	}

	set selectedSeasonIndex(value: number) {
		if (
			value !== null &&
			(value < 0 || value >= this.$tStore.getters.seasons.length)
		) {
			return
		}
		this.$tStore.commit(MutationTypes.SET_SELECTED_SEASON, value)
	}

	get scaleMin() {
		return this.trendView === 'clim'
			? 0
			: this.trendView === 'seasAnoms'
			? -25
			: -1
	}

	get scaleMax() {
		return this.trendView === 'clim'
			? 100
			: this.trendView === 'seasAnoms'
			? 25
			: 1
	}

	get palette() {
		return this.trendView === 'clim' ? this.rainPalette : this.divergingPalette
	}

	get gradientStr() {
		const gradSteps = []
		for (let i = 0; i < this.palette.steps; i++) {
			gradSteps.push(
				`rgb(${this.palette.red[i]}, ${this.palette.green[i]}, ${this.palette.blue[i]})`
			)
		}
		return gradSteps.join(',')
	}

	prevSeason() {
		if (this.selectedSeasonIndex) {
			this.selectedSeasonIndex = this.selectedSeasonIndex - 1
		}
	}

	nextSeason() {
		if (this.selectedSeasonIndex) {
			this.selectedSeasonIndex = this.selectedSeasonIndex + 1
		}
	}

	get bgLayer() {
		return {
			name: 'Stadia OSM Bright',
			url: 'https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png',
			attribution:
				'&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
		}
	}

	get covJson() {
		if (this.trendView === 'clim') {
			return this.climatologyCovJson
		} else if (this.trendView === 'enAnoms') {
			return this.enClimatologyCovJson
		} else if (this.trendView === 'lnAnoms') {
			return this.lnClimatologyCovJson
		} else {
			return this.anomalyCovJson
		}
	}

	climatologyCovJson: any = null
	climatologyContours: number[][][] = []
	enClimatologyCovJson: any = null
	lnClimatologyCovJson: any = null
	anomalyCovJson: any = null

	async mounted() {
		const climatologyUrl =
			process.env.NODE_ENV === 'production'
				? `${window.location.origin}/vp-cloud-band-explorer/data/cb_climatology.covjson`
				: `${window.location.origin}/data/cb_climatology.covjson`
		CovJSON.read(climatologyUrl).then((covJson: any) => {
			this.climatologyCovJson = covJson
			covJson.loadDomain().then((domain: any) => {
				const xVals = domain.axes.get('x').values
				const yVals = domain.axes.get('y').values
				covJson.loadRanges(covJson.parameters.keys()).then((ranges: any) => {
					// Calculate contours for climatology
					const climData = ranges.get('ndays')
					const vals = climData._ndarr.data
					const shape = climData._shape
					const climContours = contours()
						.size([shape[1], shape[0]])
						.thresholds([20, 40, 60, 80])(vals)
					for (let contour of climContours) {
						for (let polygons of contour.coordinates) {
							for (let polygon of polygons) {
								const mappedCoords = []
								for (let coord of polygon) {
									let xVal, yVal
									if (coord[0] <= 0) {
										// If it's out of bounds, use the lowest value
										xVal = xVals[0]
									} else if (coord[0] >= xVals.length - 1) {
										// If it's out of bounds, use the highest value
										xVal = xVals[xVals.length - 1]
									} else if (Math.round(coord[0]) === coord[0]) {
										// If it's an integer index, use it
										xVal = xVals[coord[0]]
									} else {
										// Otherwise, interpolate between the two nearest values
										const xCoord = coord[0]
										const amountAbove = Math.ceil(xCoord) - xCoord
										const amountBelow = xCoord - Math.floor(xCoord)
										xVal =
											xVals[Math.ceil(xCoord)] * amountBelow +
											xVals[Math.floor(xCoord)] * amountAbove
									}
									if (coord[1] < 0) {
										// If it's out of bounds, use the lowest value
										yVal = yVals[0]
									} else if (coord[1] >= yVals.length - 1) {
										// If it's out of bounds, use the highest value
										yVal = yVals[yVals.length - 1]
									} else if (Math.round(coord[1]) === coord[1]) {
										// If it's an integer index, use it
										yVal = yVals[coord[1]]
									} else {
										// Otherwise, interpolate between the two nearest values
										const yCoord = coord[1]
										const amountAbove = Math.ceil(yCoord) - yCoord
										const amountBelow = yCoord - Math.floor(yCoord)
										yVal =
											yVals[Math.ceil(yCoord)] * amountBelow +
											yVals[Math.floor(yCoord)] * amountAbove
									}

									const c = [yVal, xVal]

									mappedCoords.push(c)
								}

								this.climatologyContours.push(mappedCoords)
							}
						}
					}
				})
			})
		})
		const enClimatologyUrl =
			process.env.NODE_ENV === 'production'
				? `${window.location.origin}/vp-cloud-band-explorer/data/cb_en_climatology.covjson`
				: `${window.location.origin}/data/cb_en_climatology.covjson`
		CovJSON.read(enClimatologyUrl).then((covJson: any) => {
			this.enClimatologyCovJson = covJson
		})
		const lnClimatologyUrl =
			process.env.NODE_ENV === 'production'
				? `${window.location.origin}/vp-cloud-band-explorer/data/cb_ln_climatology.covjson`
				: `${window.location.origin}/data/cb_ln_climatology.covjson`
		CovJSON.read(lnClimatologyUrl).then((covJson: any) => {
			this.lnClimatologyCovJson = covJson
		})
		this.getAnomalyCovJson()
	}

	@Watch('selectedSeasonIndex')
	async getAnomalyCovJson() {
		this.$store.commit('SET_LOADING')
		// @ts-ignore
		const year =
			this.selectedSeasonIndex === null ? 1979 : this.selectedSeasonIndex + 1979
		const anomalyUrl =
			process.env.NODE_ENV === 'production'
				? `${window.location.origin}/vp-cloud-band-explorer/data/cb_clim_anom_${year}.covjson`
				: `${window.location.origin}/data/cb_clim_anom_${year}.covjson`

		this.anomalyCovJson = await CovJSON.read(anomalyUrl)
		this.$store.commit('SET_FINISHED_LOADING')
	}
}
